import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProductDetail } from '../../types/product';
import { FiShoppingCart } from 'react-icons/fi';
import { useCart } from '../../context/CartContext';
import { formatCurrency } from '../../utils/currency';
import { getLocalizedField } from '../../utils/localize';
import type { SupportedLanguage } from '../../i18n';
import CCOptionsEditModal from '../Cart/CCOptionsEditModal';

const Card = styled.article`
  background-color: var(--white);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  padding-top: 75%;
  background-color: var(--gray-light);
  overflow: hidden;
`;

const ProductImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CardContent = styled.div`
  padding: 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ProductName = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
  font-weight: 600;
`;

const ProductDescription = styled.p`
  font-size: 0.9rem;
  color: var(--gray-dark);
  margin-bottom: 1rem;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
`;

const Price = styled.span`
  font-size: 1.25rem;
  font-weight: bold;
  color: var(--primary-color);
`;

const AddToCartButton = styled.button`
  background-color: var(--primary-color);
  color: var(--white);
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: background-color 0.3s;
  font-size: 0.9rem;

  &:hover:not(:disabled) {
    background-color: var(--secondary-color);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Skeleton = styled.div`
  background: linear-gradient(
    90deg,
    var(--gray-light) 0%,
    #f0f0f0 50%,
    var(--gray-light) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

const SkeletonCard = styled(Card)`
  cursor: default;

  &:hover {
    transform: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

interface ProductCardProps {
  product?: ProductDetail;
  loading?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, loading }) => {
  const { addToCart } = useCart();
  const { t, i18n } = useTranslation();
  // Mirrors the LanguageToggle.tsx pattern: i18next's language can be a
  // region-qualified tag or unset, so narrow to the two v1-supported values.
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';
  const [isAdding, setIsAdding] = useState(false);
  const [showSizeModal, setShowSizeModal] = useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleAddToCart = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent double clicks
    if (isAdding || !product) {
      return;
    }

    // Products with a REQUIRED option group must have a selection made
    // before being added to cart (the server rejects the line otherwise).
    // Quick-add is only for products with no required group.
    if (product.has_required_options) {
      setShowSizeModal(true);
      return;
    }

    setIsAdding(true);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Add to cart only once
    addToCart(product, 1);

    // Reset the flag after a short delay
    timeoutRef.current = setTimeout(() => {
      setIsAdding(false);
    }, 500);
  }, [isAdding, product, addToCart]);

  if (loading || !product) {
    return (
      <SkeletonCard>
        <Skeleton style={{ paddingTop: '75%' }} />
        <CardContent>
          <Skeleton style={{ height: '1.5rem', marginBottom: '0.5rem' }} />
          <Skeleton style={{ height: '2.5rem', marginBottom: '1rem' }} />
          <CardFooter>
            <Skeleton style={{ height: '1.5rem', width: '60px' }} />
            <Skeleton style={{ height: '36px', width: '100px' }} />
          </CardFooter>
        </CardContent>
      </SkeletonCard>
    );
  }

  const handleNavigate = (e: React.MouseEvent) => {
    // Only navigate if the click target is not the button
    const target = e.target as HTMLElement;
    if (target.closest('button')) {
      e.preventDefault();
      return;
    }
  };

  return (
    <Card>
      <Link
        to={`/products/${product.product_id}`}
        onClick={handleNavigate}
        style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
      >
        <ImageWrapper>
          <ProductImage
            src={
              product.images && product.images.length > 0
                ? product.images.find(img => img.is_primary)?.image_url || product.images[0].image_url
                : "https://images.unsplash.com/photo-1578985545062-69928b1d9587"
            }
            alt={getLocalizedField(product, 'name', language) as string}
            loading="lazy"
          />
        </ImageWrapper>
        <CardContent>
          <ProductName>{getLocalizedField(product, 'name', language) as string}</ProductName>
          <ProductDescription>{getLocalizedField(product, 'description', language) as string | undefined}</ProductDescription>
          <CardFooter>
            <Price>{formatCurrency(product.base_price)}</Price>
            <AddToCartButton
              type="button"
              onClick={handleAddToCart}
              disabled={isAdding}
            >
              <FiShoppingCart />
              <span>{isAdding ? t('products.adding') : t('products.add')}</span>
            </AddToCartButton>
          </CardFooter>
        </CardContent>
      </Link>
      {showSizeModal && product && (
        <CCOptionsEditModal
          item={{ product, quantity: 1 }}
          onConfirm={(ccOptions, price) => {
            addToCart(product, 1, undefined, price, undefined, undefined, ccOptions);
            setShowSizeModal(false);
          }}
          onClose={() => setShowSizeModal(false)}
        />
      )}
    </Card>
  );
};

export default ProductCard;